import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LogError, Metadata, RunView } from '@memberjunction/core';
import { arrowLeftIcon, plusIcon, moreVerticalIcon, pencilIcon, fullscreenExitIcon } from '@progress/kendo-svg-icons';
import { OrganizationContactEntityExtended, StripeCustomerEntity, SubscriptionEntityExtended } from 'mj_generatedentities'; // or your own entity definition
import { InstanceEntity } from 'mj_generatedentities';     // or your own instance entity definition
import { InstanceContactEntityExtended } from 'mj_generatedentities';         // or your own user/contact entity
import { UserContextService } from 'src/app/core/services/user-context.service';
import { SharedService } from 'src/app/core/services/shared-service';
import { StripePaidInvoice, StripePaymentMethodData, StripeUpcomingInvoice, SubscriptionPlanService, SubscriptionPreviewOptions } from 'src/app/core/services/subscription-plan.service';
import { STRIPE_CARD_BRAND_ICONS } from 'src/app/core/util';

interface SubscriptionDetails {
  PlanName: string;
  Price: number;
  BillingPeriod: string;
  TotalAmount?: number;
}

interface PaymentMethodData extends StripePaymentMethodData {
  cardIconUrl: string;
}

@Component({
  selector: 'app-subscription-detail',
  templateUrl: './subscription-detail.component.html',
  styleUrls: ['./subscription-detail.component.css']
})
export class SubscriptionDetailComponent implements OnInit {
  subscriptionID!: string;
  subscription: SubscriptionEntityExtended | null = null;

  subscriptionDetails: SubscriptionDetails = null;
  subscriptionHistory: any[] = [];

  // Instances data
  instances: InstanceEntity[] = [];
  // Users data
  users: InstanceContactEntityExtended[] = [];

  customerName: string = '';
  currentPeriod: string | null = null;
  upcomingInvoice: StripeUpcomingInvoice | null = null;
  paidInvoices: StripePaidInvoice[] = [];
  paymentMethod: PaymentMethodData | null = null;

  // For label editing
  isEditingLabel = false;
  editedLabel: string = '';

  // "Cancel Subscription" confirmation dialog
  showCancelDialog = false;

  isLoading = true;
  loadingMessage = 'Loading subscription details...';

  // "Remove user" confirmation dialog
  showRemoveUserDialog = false;
  isPreviewLoaded = false;
  userToRemove: InstanceContactEntityExtended | null = null;
  previewOptions: SubscriptionPreviewOptions = null;

  arrowLeftIcon = arrowLeftIcon;
  plusIcon = plusIcon;
  moreVerticalIcon = moreVerticalIcon;
  pencilIcon = pencilIcon;
  fullscreenExitIcon = fullscreenExitIcon;

  actions = [
    { text: 'Update Subscription', action: 'update' },
    { text: 'Edit Payment Method', action: 'edit-payment' },
    //{ separator: true },
    { text: 'Cancel Subscription', action: 'cancel', cssClass: 'red-item' }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private sharedService: SharedService,
    private userContextService: UserContextService,
    private subscriptionService: SubscriptionPlanService
  ) {}

  async ngOnInit(): Promise<void> {
    this.subscriptionID = this.route.snapshot.paramMap.get('subscriptionId')!;
    this.loadData();
  }

  async loadData(): Promise<void> {
    this.isLoading = true;
    this.loadingMessage = 'Loading subscription details...';
    // 1. Extract subscription ID from URL
    this.subscriptionID = this.route.snapshot.paramMap.get('subscriptionId')!;
    this.subscription = this.userContextService.Subscriptions?.find(sub => sub.ID === this.subscriptionID);

    if (!this.subscription) {
      console.error('Subscription not found');
      this.sharedService.DisplayNotification('Subscription not found', 'error');
      this.router.navigate(['/settings/subscription-list']);
      return;
    }
    
    // 2. Load subscription detail from your backend
    const p1 = this.loadSubscriptionDetails();
    const p2 = this.loadCustomerDetails();
    const p3 = this.loadSubscriptionData();
    const p4 = this.loadSubscriptionHistoryData();

    await Promise.all([p1, p2, p3, p4]);

    this.isLoading = false;
  }

  async loadSubscriptionDetails(): Promise<void> {
    try {
      const subscription = this.subscription;
      const plan = this.subscriptionService.Plans.find(p => p.Prices.some(pp => pp.ID === subscription.PlanPriceID));
      const price = plan?.Prices.find(p => p.ID === subscription.PlanPriceID);
      const billingCycle = this.subscriptionService.GetBillingCycle(subscription);
      const instances = await this.subscriptionService.GetInstances(subscription);
      const users = await this.getUsers(instances);

      this.subscription = subscription;
      this.instances = instances;
      this.users = users;
      this.subscriptionDetails = {
        PlanName: plan.Plan.Name || '',
        Price: price?.Price || 0,
        BillingPeriod: billingCycle?.Name === 'Annual' ? 'Year' : billingCycle?.Name === 'Monthly' ? 'Month' : '',
        TotalAmount: this.subscriptionService.CalculateRecuringInvoiceAmount(subscription).amount
      };

      this.editedLabel = this.subscription?.Label || '';
    } catch (err) {
      console.error('Failed to load subscription detail:', err);
    }
  }

  async loadCustomerDetails(): Promise<void> {
    // Load customer details from your backend
    const subscription = this.subscription;

    const md = new Metadata();
    const pCustomer = md.GetEntityObject<StripeCustomerEntity>('Stripe Customers');
    const pOrgContact = md.GetEntityObject<OrganizationContactEntityExtended>('Organization Contacts');

    const [customer, orgContact] = await Promise.all([pCustomer, pOrgContact]);
    if (!(await customer.Load(subscription.StripeCustomerID))) return;
    if (!(await orgContact.Load(customer.OrganizationContactID))) return;

    this.customerName = orgContact.Contact;
  }

  async loadSubscriptionData(): Promise<void> {
    // Load subscription data from backend
    // Example: upcoming invoice, invoice history, current period, payment method
    if (!this.subscription) return;

    try {
      const data = await this.subscriptionService.GetStripeSubscriptionData(this.subscription);
      if (!data) {
        console.error('Failed to load subscription data');
        return;
      }

      // Map the data to the UI
      if (data.currentPeriod) {
        const start = new Date(data.currentPeriod.start);
        const end = new Date(data.currentPeriod.end);
        this.currentPeriod = `${start.toLocaleDateString()} - ${end.toLocaleDateString()}`;
      }
      if (data.upcomingInvoice) {
        this.upcomingInvoice = data.upcomingInvoice;
      }
      if (data.paymentMethod && data.paymentMethod.card) {
        this.paymentMethod = {
          ...data.paymentMethod,
          cardIconUrl: this.getCardBrandIcon(data.paymentMethod.card.brand)
        };
      }
      if (data.invoiceHistory) {
        this.paidInvoices = data.invoiceHistory;
      }
    } catch (error) {
      console.error('Error loading Stripe subscription data:', error);
    }
  }

  private getCardBrandIcon(brand: string): string {
    const normalizedBrand = brand?.toLowerCase() || 'unknown';
    return STRIPE_CARD_BRAND_ICONS[normalizedBrand] || STRIPE_CARD_BRAND_ICONS.unknown;
  }

  async loadSubscriptionHistoryData(): Promise<void> {
    // Load subscription history data from backend
    if (!this.subscription) return;

    const rv = new RunView();
    const results = await rv.RunView({
      EntityName: 'Subscription Histories',
      ExtraFilter: `SubscriptionID = '${this.subscription.ID}'`,
      OrderBy: 'ChangeDate DESC',
    });

    if (results.Success && results.Results) {
      this.subscriptionHistory = results.Results;
    }
  }

  onBack(): void {
    this.router.navigate(['/settings/subscription-list']);
  }

  async goToBilling(): Promise<void> {
    this.loadingMessage = 'Redirecting to payment method editor...';
    this.isLoading = true;

    // Navigate to the edit-payment route instead of using the Stripe billing portal
    this.router.navigate(['/settings/subscription', this.subscriptionID, 'edit-payment']);
    
    this.isLoading = false;
  }

  async onActionSelect(e: any): Promise<void> {
    if (!e || !e.action) {
      // Possibly a separator or an invalid item
      return;
    }
    const action = e.action;
  
    switch (action) {
      case 'update':
        this.router.navigate(['/settings/subscription', this.subscriptionID, 'update']);
        break;
      case 'edit-payment':
        this.isLoading = true;
        await this.goToBilling();
        this.isLoading = false;
        break;
      case 'cancel':
        this.showCancelDialog = true;
        break;
    }
  }

  async confirmCancelSubscription(): Promise<void> {
    // Called when user confirms "Cancel Subscription"
    if (!this.subscription) return;

    this.loadingMessage = 'Canceling subscription...';
    this.isLoading = true;

    try {
      const contactID = this.userContextService.Contact.ID;
      await this.subscriptionService.UpdateSubscription(this.subscription, contactID, { CancelSubscription: true });

    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      this.sharedService.DisplayNotification('Failed to cancel subscription', 'error');
    } finally {
      this.isLoading = false;
      this.showCancelDialog = false;
    }

    try {
      this.subscription.Status = 'Canceled';
      if (await this.subscription.Save()) {
        this.sharedService.DisplayNotification('Subscription canceled successfully', 'success');
        this.router.navigate(['/settings/subscription-list']);
      } else {
        this.sharedService.DisplayNotification('Failed to cancel subscription', 'error');
      }
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      this.sharedService.DisplayNotification('Failed to cancel subscription', 'error');
    } finally {
      this.isLoading = false;
      this.showCancelDialog = false;
    }
  }

  onEditLabel(): void {
    this.editedLabel = this.subscription?.Label || '';
    this.isEditingLabel = true;
  }

  async onSaveLabel(): Promise<void> {
    if (!this.subscription) return;

    this.loadingMessage = 'Updating label...';
    this.isLoading = true;

    try {
      // Update the label in the backend
      this.subscription.Label = this.editedLabel;
      if (await this.subscription.Save()) {
        this.sharedService.DisplayNotification('Label updated successfully', 'success');
      } else {
        const errorMessage = this.subscription.LatestResult.Errors?.map(e => e.Message).join('\n') || this.subscription.LatestResult?.Message;
        LogError(`Failed to update subscription label:\n${errorMessage}`);
        this.sharedService.DisplayNotification('Failed to update label', 'error');
      }
    } catch (error) {
      console.error('Failed to update label:', error);
      this.sharedService.DisplayNotification('Failed to update label', 'error');
    } finally {
      this.isLoading = false;
      this.isEditingLabel = false;
    }
  }

  onCancelLabelEdit(): void {
    this.isEditingLabel = false;
    this.editedLabel = this.subscription?.Label || '';
  }

  async onCreateInstance(): Promise<void> {
    // Verify that we can create a new instance
    if (!this.subscription) {
      console.error('Subscription not found');
      this.sharedService.DisplayNotification('Subscription not found', 'error');
      return;
    }

    const newCount = this.subscription.InstanceCount + 1;
    const result = await this.subscription.ValidateInstanceCountChange(newCount);

    if (!result.IsValid) {
      this.subscriptionService.OpenUpgradeModal(this.subscription, {
        MaxInstance: newCount
      })
    } else {
      this.isLoading = true;
      this.loadingMessage = 'Creating new instance...';
      console.log("Creating new instance...");

      try {
        const contactID = this.userContextService.Contact.ID;
        const result = await this.subscriptionService.UpdateSubscription(this.subscription, this.userContextService.Contact.ID, { NewInstanceCount: newCount });
        
        if (result.Success) {
          this.sharedService.DisplayNotification('New instance created successfully', 'success');
          await this.loadSubscriptionDetails(); // Refresh the subscription details
        } else {
          LogError(`Failed to create new instance:\n${result.Message}`);
          this.sharedService.DisplayNotification('Failed to create new instance', 'error');
        }
        
      } catch (error) {
        console.error('Failed to update subscription for new instance:', error);
        this.sharedService.DisplayNotification('Failed to create new instance', 'error');
      } finally {
        this.isLoading = false;
      }
      this.isLoading = false;
    }
  }

  getInstanceActions(instance: InstanceEntity): string[] {
    // Return the available actions based on instance.Status
    // Example statuses: Active, Locked, Archived, etc.
    const actions: string[] = [];
    if (instance.Status !== 'Locked') {
      actions.push('Lock');
    }
    if (instance.Status === 'Locked') {
      actions.push('Archive');
    }
    if (instance.Status === 'Locked') {
      actions.push('Activate');
    }
    return actions;
  }

  onInstanceActionSelect(action: string, instance: InstanceEntity): void {
    // Lock, Archive, or Activate the instance
    console.log(`Instance action: ${action} for ${instance.Name}`);
    // Perform the action (call a service, etc.)
  }

  onClickInstanceName(instanceID: string): void {
    // Navigate to instance settings page
    const url = `/settings/instance/${instanceID}/details`;
    this.router.navigate([url]);
  }

  onRemoveUser(user: InstanceContactEntityExtended): void {
    this.userToRemove = user;
    this.previewOptions = { NewUserCount: this.subscription.CurrentUserCount - 1 };
    this.showRemoveUserDialog = true;
  }

  onPreviewLoaded(isLoaded: boolean): void {
    this.isPreviewLoaded = isLoaded;
  }

  async confirmRemoveUser(): Promise<void> {
    if (!this.userToRemove) return;

    this.loadingMessage = 'Removing user...';
    this.isLoading = true;

    try {
      this.userToRemove.IsActive = false;
      if (await this.userToRemove.Save()) {
        this.sharedService.DisplayNotification('User removed successfully', 'success');
        this.users = this.users.filter(u => u.ID !== this.userToRemove!.ID);
      } else {
        const errorMessage = this.userToRemove.LatestResult.Errors?.map(e => e.Message).join('\n') || this.userToRemove.LatestResult.Message;
        LogError(`Failed to remove user:\n${errorMessage}`);
        this.sharedService.DisplayNotification('Failed to remove user', 'error');
      }
    } finally {
      this.isLoading = false;
      this.isPreviewLoaded = false;
      this.showRemoveUserDialog = false;
      this.userToRemove = null;
      this.previewOptions = null;
    }
  }

  getHistoryValues(dataItem: any): { oldValue: any; newValue: any } {
    let result = { oldValue: '—', newValue: '—' };
  
    switch (dataItem.ChangeType) {
      case 'User Count':
        result.oldValue = dataItem.OldUserCount;
        result.newValue = dataItem.NewUserCount;
        break;
      case 'Instance Removed':
      case 'Instance Added':
        result.oldValue = dataItem.OldInstanceCount;
        result.newValue = dataItem.NewInstanceCount;
        break;
      case 'Downgrade':
      case 'Upgrade':
        result.oldValue = this.subscriptionService.Plans.find((p) => p.Prices.some((pp) => pp.ID === dataItem.OldPlanPriceID))?.Plan.Name || '—';
        result.newValue = this.subscriptionService.Plans.find((p) => p.Prices.some((pp) => pp.ID === dataItem.NewPlanPriceID))?.Plan.Name || '—';
        break;
      case 'Billing Cycle':
        const oldPrice = this.subscriptionService.Plans.flatMap((p) => p.Prices).find((p) => p.ID === dataItem.OldPlanPriceID);
        const newPrice = this.subscriptionService.Plans.flatMap((p) => p.Prices).find((p) => p.ID === dataItem.NewPlanPriceID);
        result.oldValue = oldPrice?.BillingCycle || '—';
        result.newValue = newPrice?.BillingCycle || '—';
        break;
      default:
        break;
    }
  
    return result;
  }
  

  private async getUsers(instances: InstanceEntity[]): Promise<InstanceContactEntityExtended[]> {
    // Fetch users from instances
    const rv = new RunView();
    const results = await rv.RunView<InstanceContactEntityExtended>({
      EntityName: 'Instance Contacts',
      ExtraFilter: `InstanceID IN (${instances.map(i => `'${i.ID}'`).join(',')}) AND IsActive = 1`,
      ResultType: 'entity_object'
    });

    if (results.Success && results.Results) {
      const activeUsers = results.Results.filter(u => u.IsActive);
      return activeUsers;
    }
    return [];
  }
}
